import { Checkbox, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useSelector } from 'react-redux';
import { formatNumber, formatToDateOnly } from 'utils/utils';
import { useState } from 'react';

type Props = {
	isLoading: boolean;
	handleCheckboxChange: (id: string, value: boolean, name: string) => void;
	employeeData: any;
	onlyView: boolean;
	tableChangeHandler: (pagination: any, filters: any, sorter: any) => void;
	handleBulkCheckboxChange: (
		type: 'leave' | 'gratuity',
		value: boolean
	) => void;

	showSteps: boolean;
};

const LeaveGratuityPayTable = (props: Props) => {
	const {
		isLoading,
		onlyView,
		handleCheckboxChange,
		employeeData,
		tableChangeHandler,
		showSteps,
		handleBulkCheckboxChange,
	} = props;

	const { data } = useSelector((state: any) => state?.userProfile);

	const { conversionRate, usdCurrencyToggle, isUsdCurrency } = useSelector(
		(state: any) => state.usdCurrencyToggle
	);

	const [selectAllLeave, setSelectAllLeave] = useState(false);
	const [selectAllGratuity, setSelectAllGratuity] = useState(false);

	const FormattedValue = (value: any) => {
		if (!usdCurrencyToggle && isUsdCurrency) {
			return formatNumber(Number(value), data?.CompanyCurrencies, true);
		} else {
			return formatNumber(
				Number(value * (conversionRate ?? 1)),
				data?.CompanyCurrencies
			);
		}
	};

	const handleSelectAll = (type: 'leave' | 'gratuity', checked: boolean) => {
		if (type === 'leave') {
			setSelectAllLeave(checked);
			handleBulkCheckboxChange('leave', checked);
		} else {
			setSelectAllGratuity(checked);
			handleBulkCheckboxChange('gratuity', checked);
		}
	};

	const columns: ColumnsType<any> = [
		{
			title: 'Employee Name',
			dataIndex: 'employeeName',
			key: 'employeeName',
			width: '16%',
			className: 'bg-white',
			render: (_: any, record: any) => (
				<>
					<span>{record.employeeName}</span> |{' '}
					<span className="color-purple">{record.employeeCode}</span>
				</>
			),
		},
		{
			title: 'Last Working Day',
			dataIndex: 'lastWorkingDay',
			className: 'bg-white',
			key: 'lastWorkingDay',
			width: '12%',
			render: (text: any) => (text ? formatToDateOnly(text) : '-'),
		},
		{
			title: 'Leave Pay',
			className: 'bg-white',
			children: [
				{
					title: (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								gap: 8,
							}}
						>
							{!onlyView && (
								<Checkbox
									checked={selectAllLeave}
									onChange={(e) =>
										handleSelectAll(
											'leave',
											e.target.checked
										)
									}
								/>
							)}
							<span>Add Leave Pay to Payroll</span>
						</div>
					),
					dataIndex: 'isLeaveIncluded',
					key: 'isLeaveIncluded',
					width: '15%',
					align: 'center',
					className: 'bg-white',
					render: (_: any, record: any) => (
						<Checkbox
							checked={record.isLeaveIncluded || false}
							disabled={onlyView || record.alreadyLeavePaid}
							onChange={(e) =>
								handleCheckboxChange(
									record.employeeId,
									e.target.checked,
									'isLeaveIncluded'
								)
							}
						/>
					),
				},
				{
					title: 'Leave Days',
					dataIndex: 'leaveDays',
					key: 'leaveDays',
					width: '8%',
					render: (text: any) => `${text} days`,
					className: 'bg-white',
				},
				{
					title: 'Leave Amount',
					dataIndex: 'leaveAmount',
					key: 'leaveAmount',
					width: '10%',
					className: 'bg-white',
					render: (value: any) => FormattedValue(value),
				},
			],
		},
		{
			title: 'Gratuity Pay',
			className: 'bg-white',
			children: [
				{
					title: (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								gap: 8,
							}}
						>
							{!onlyView && (
								<Checkbox
									checked={selectAllGratuity}
									onChange={(e) =>
										handleSelectAll(
											'gratuity',
											e.target.checked
										)
									}
								/>
							)}
							<span>Add Gratuity Pay to Payroll</span>
						</div>
					),
					dataIndex: 'isGratuityIncluded',
					key: 'isGratuityIncluded',
					className: 'bg-white',
					width: '15%',
					align: 'center',
					render: (_: any, record: any) => (
						<Checkbox
							checked={record.isGratuityIncluded || false}
							disabled={onlyView || record.alreadyGratuityPaid}
							onChange={(e) =>
								handleCheckboxChange(
									record.employeeId,
									e.target.checked,
									'isGratuityIncluded'
								)
							}
						/>
					),
				},
				{
					title: 'Gratuity Amount',
					dataIndex: 'gratuityAmount',
					key: 'gratuityAmount',
					width: '12%',
					render: (value: any) => FormattedValue(value),
					className: 'bg-white',
				},
			],
		},
	];

	return (
		<>
			<div className="payroll-steps-table">
				<Table
					bordered
					dataSource={employeeData}
					columns={columns}
					pagination={false}
					scroll={{
						y: showSteps
							? 'calc(100vh - 500px)'
							: 'calc(100vh - 375px)',
					}}
					rowKey={(record) => record.employeeId}
					loading={isLoading}
					onChange={tableChangeHandler}
				/>
			</div>
		</>
	);
};

export default LeaveGratuityPayTable;
